import { Loader2, Send } from "lucide-react";
import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "../../../lib/utils";

interface Props {
  onSubmit: (message: string) => void | Promise<void>;
  disabled?: boolean;
  isProcessing?: boolean;
  placeholder?: string;
}

const MAX_LENGTH = 4000;
const MIN_HEIGHT = 24;
const MAX_HEIGHT = 200;

export const PublicChatInput = ({
  onSubmit,
  disabled,
  isProcessing,
  placeholder,
}: Props) => {
  const [value, setValue] = useState("");
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize the textarea to fit content.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, MIN_HEIGHT), MAX_HEIGHT)}px`;
  }, [value]);

  // Keep focus on the textarea: focus on mount, and refocus whenever the
  // input becomes enabled again (e.g. after a response finishes streaming) so
  // the user doesn't have to re-click before typing the next message.
  // preventScroll matters on the WordPress embed: without it, focusing the
  // below-the-fold input on mount scrolls the whole HOST page down to the
  // widget, hiding the page's own above-the-fold content (NOV bug: novopsych
  // .com pages landing scrolled to the Jan input).
  useEffect(() => {
    if (!disabled) taRef.current?.focus({ preventScroll: true });
  }, [disabled]);

  const canSubmit = value.trim().length > 0 && !disabled && !isProcessing;

  const send = async () => {
    const trimmed = value.trim();
    if (!trimmed || !canSubmit) return;
    setValue("");
    await onSubmit(trimmed);
  };

  const onSubmitForm = (e: FormEvent) => {
    e.preventDefault();
    void send();
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <form onSubmit={onSubmitForm} className="w-full">
      <div
        className={cn(
          "flex flex-col gap-2 p-3 bg-white border border-slate-200 rounded-xl shadow-sm",
          "focus-within:border-slate-300 focus-within:ring-2 focus-within:ring-slate-100",
          disabled && "opacity-60"
        )}
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={taRef}
            data-gramm="false"
            value={value}
            onChange={(e) => setValue(e.target.value.slice(0, MAX_LENGTH))}
            onKeyDown={onKey}
            rows={1}
            disabled={disabled}
            placeholder={
              isProcessing
                ? "Jan is responding..."
                : (placeholder ?? "Type your question")
            }
            className={cn(
              // 16px on phones stops iOS Safari from zooming the viewport when
              // the textarea is focused (it auto-zooms any input below 16px);
              // shrink back to the 14px design size from `sm` up.
              "flex-1 resize-none bg-transparent text-base sm:text-sm leading-relaxed",
              "placeholder:text-slate-400 focus:outline-none",
              "disabled:cursor-not-allowed"
            )}
            style={{
              minHeight: `${MIN_HEIGHT}px`,
              maxHeight: `${MAX_HEIGHT}px`,
            }}
            aria-label="Chat message input"
          />
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              "flex-shrink-0 p-2 rounded-lg transition-colors",
              canSubmit
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            )}
            aria-label="Send message"
          >
            {isProcessing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </form>
  );
};
